import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { User, LogOut, MessageCircle, Search, Home, Settings, Shield } from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'
import { ThemeToggle } from '../ThemeToggle'

const Header = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { user, isAuthenticated, logout } = useAuthStore()

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang)
  }

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  return (
    <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and brand */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="/logo_tiering.png" 
                alt="Tiering.de Logo" 
                className="w-8 h-8 object-contain"
              />
              <span className="text-xl font-semibold text-gray-900 dark:text-white">tiering.de</span>
            </Link>
            <div className="hidden sm:block ml-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('header.tagline')}</p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="hidden md:flex space-x-8">
            <Link
              to="/"
              className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white px-3 py-2 rounded-md text-sm font-medium flex items-center space-x-1"
            >
              <Home size={16} />
              <span>{t('navigation.home')}</span>
            </Link>
            <Link
              to="/search"
              className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white px-3 py-2 rounded-md text-sm font-medium flex items-center space-x-1"
            >
              <Search size={16} />
              <span>{t('navigation.search')}</span>
            </Link>
            <Link
              to="/how-it-works"
              className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white px-3 py-2 rounded-md text-sm font-medium"
            >
              {t('navigation.howItWorks')}
            </Link>
            <Link
              to="/suggestions"
              className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white px-3 py-2 rounded-md text-sm font-medium"
            >
              {t('navigation.suggestions')}
            </Link>
          </nav>

          {/* Right side actions */}
          <div className="flex items-center space-x-4">
            {/* Theme toggle */}
            <ThemeToggle />
            {/* Language selector */}
            <div className="hidden sm:block">
              <select
                value={i18n.language}
                onChange={(e) => handleLanguageChange(e.target.value)}
                className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 dark:text-white"
              >
                <option value="en">EN</option>
                <option value="de">DE</option>
              </select>
            </div>

            {/* User menu */}
            {isAuthenticated && user ? (
              <div className="flex items-center space-x-3">
                <Link
                  to="/messages"
                  className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white p-2 rounded-md"
                >
                  <MessageCircle size={18} />
                </Link>
                <Link
                  to="/profile"
                  className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white p-2 rounded-md"
                >
                  <Settings size={18} />
                </Link>
                {user.is_admin && (
                  <Link
                    to="/admin"
                    className="text-orange-600 hover:text-orange-700 p-2 rounded-md"
                    title="Admin Panel"
                  >
                    <Shield size={18} />
                  </Link>
                )}
                <Link
                  to="/dashboard"
                  className="flex items-center space-x-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                >
                  <User size={16} />
                  <span className="hidden sm:block">
                    {user.username || t('user.you')}
                  </span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white p-2 rounded-md"
                  title={t('navigation.signOut')}
                >
                  <LogOut size={18} />
                </button>
              </div>
            ) : (
              <Link
                to="/auth"
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                {t('navigation.signIn')}
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Mobile navigation menu (could be expanded with state management) */}
      <div className="md:hidden px-4 pb-3 border-t border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-4 mt-3">
          <Link
            to="/"
            className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium flex items-center space-x-1"
          >
            <Home size={16} />
            <span>{t('navigation.home')}</span>
          </Link>
          <Link
            to="/search"
            className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium flex items-center space-x-1"
          >
            <Search size={16} />
            <span>{t('navigation.search')}</span>
          </Link>
        </nav>
      </div>
    </header>
  )
}

export default Header